import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Download, RefreshCw, Users, Clock, Activity, BarChart3,
  Search, ChevronLeft, ChevronRight,
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  BarChart, Bar, LabelList,
} from 'recharts'
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig,
} from '@/shared/components/ui/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Input } from '@/shared/components/ui/input'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import { useAsistenciasData } from '@/modules/registros/hooks/useAsistenciasData'
import { tokenStorage } from '@/api/client'
import { API_BASE_URL } from '@/shared/lib/constants'

const TONAL_BLUE = '#57a6d8'
const TONAL_PURPLE = '#8d82bc'
const TONAL_PINK = '#eb6587'
const TONAL_GREEN = '#2fbc80'

function getEcuadorDateStr(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' })
}

// Encabezado legible para una columna dinámica de la tabla de asistencias.
function formatColumnLabel(name: string): string {
  if (name === 'fecha_asistencia') return 'Fecha/Hora'
  return name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Valor formateado de una celda según la columna.
function formatCellValue(columna: string, valor: string | number | null): string {
  if (valor === null || valor === undefined || valor === '') return '-'
  if (columna === 'fecha_asistencia') {
    return new Date(String(valor)).toLocaleString('es-EC', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }
  return String(valor)
}

function isEventToday(fechaEvento: string | null | undefined): boolean {
  if (!fechaEvento) return false
  return fechaEvento.slice(0, 10) === getEcuadorDateStr()
}

function getEstadoEvento(
  fecha: string | null | undefined,
  horaFin?: string | null,
): { label: string; color: string } {
  if (!fecha) return { label: 'Sin fecha', color: 'gray' }
  const today = getEcuadorDateStr()
  const d = fecha.slice(0, 10)
  if (d > today) return { label: 'Pendiente', color: TONAL_PURPLE }
  if (d === today) {
    if (horaFin) {
      const nowEc = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Guayaquil', hour: '2-digit', minute: '2-digit' })
      if (nowEc > horaFin.slice(0, 5)) return { label: 'Finalizado', color: TONAL_PINK }
    }
    return { label: 'En curso', color: TONAL_GREEN }
  }
  return { label: 'Finalizado', color: TONAL_PINK }
}

function AnimatedCounter({ value }: { value: number }) {
  const [display, setDisplay] = useState(0)
  const prev = useRef(0)

  useEffect(() => {
    const start = prev.current
    prev.current = value
    const diff = value - start
    if (diff === 0) return
    const duration = 1200
    const steps = Math.ceil(duration / 16)
    let step = 0
    const inc = diff / steps
    const timer = setInterval(() => {
      step++
      setDisplay(Math.round(start + inc * step))
      if (step >= steps) {
        setDisplay(value)
        clearInterval(timer)
      }
    }, 16)
    return () => clearInterval(timer)
  }, [value])

  return <span>{display.toLocaleString()}</span>
}

function FunnelChart({
  registros, confirmaciones, asistentes,
}: { registros: number; confirmaciones: number; asistentes: number }) {
  const levels = [
    { label: 'Registros', value: registros, color: TONAL_BLUE },
    { label: 'Confirmaciones', value: confirmaciones, color: TONAL_PURPLE },
    { label: 'Asistentes', value: asistentes, color: TONAL_GREEN },
  ]
  const max = Math.max(registros, 1)

  return (
    <div className="flex flex-col gap-3 py-1">
      {levels.map((level, i) => {
        const pct = Math.max((level.value / max) * 100, 8)
        const convPct = i > 0 && registros > 0
          ? ((level.value / registros) * 100).toFixed(1)
          : '100'
        return (
          <div key={level.label} className="flex flex-col gap-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{level.label}</span>
              <span>{level.value.toLocaleString()} · {convPct}%</span>
            </div>
            <div className="w-full h-7 relative">
              <div
                className="h-full rounded transition-all duration-700"
                style={{ width: `${pct}%`, background: level.color, opacity: 0.85 }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

const PAGE_SIZE = 10

export function AsistenciasDashboardPage() {
  const { configId } = useParams<{ configId: string }>()
  const navigate = useNavigate()
  const id = Number(configId)

  const { data, isLoading, error, dataUpdatedAt, isFetching } = useAsistenciasData(id)

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sortKey, setSortKey] = useState<string>('fecha_asistencia')
  const [sortAsc, setSortAsc] = useState(false)

  const handleSort = (key: string) => {
    if (sortKey === key) setSortAsc((v) => !v)
    else { setSortKey(key); setSortAsc(true) }
  }

  const filtered = (data?.asistentes ?? []).filter((a) => {
    const q = search.toLowerCase()
    if (!q) return true
    // Búsqueda genérica sobre todos los valores de la fila
    return Object.values(a).some((v) => String(v ?? '').toLowerCase().includes(q))
  })

  const sorted = [...filtered].sort((a, b) => {
    const va = String(a[sortKey] ?? '')
    const vb = String(b[sortKey] ?? '')
    return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va)
  })

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [search])

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-destructive">{(error as any)?.response?.data?.error ?? 'Error al cargar el dashboard.'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
      </div>
    )
  }

  const est = data?.estadisticas
  const estado = getEstadoEvento(est?.fecha_evento, est?.hora_fin)
  const today = isEventToday(est?.fecha_evento)

  const chartData = (data?.datos_graficos.asistencia_por_hora.labels ?? []).map((label, i) => ({
    intervalo: label,
    asistentes: data!.datos_graficos.asistencia_por_hora.data[i],
  }))

  const perfiles = data?.asistencia_por_perfil ?? []

  // Columnas visibles de la tabla (dinámicas). El backend ya devuelve el orden y
  // el filtrado según la configuración; aquí solo se renderizan.
  const columnasAsistencia = data?.columnas_asistencia ?? []
  const numColumnas = columnasAsistencia.length || 1

  const lineChartConfig: ChartConfig = {
    asistentes: { label: 'Asistentes', color: TONAL_BLUE },
  }

  const perfilChartConfig: ChartConfig = {
    total: { label: 'Total', color: TONAL_BLUE },
  }

  const perfilChartData = [...perfiles]
    .sort((a, b) => b.total - a.total)
    .map((p) => ({ perfil: p.perfil, total: p.total }))

  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const token = tokenStorage.getAccessToken()
      const res = await fetch(`${API_BASE_URL}/registros/reporte/${id}/exportar-asistentes/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `asistentes_${id}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error al exportar:', err)
    } finally {
      setIsExporting(false)
    }
  }

  const SortIcon = ({ col }: { col: string }) => (
    <span className="ml-1 text-muted-foreground cursor-pointer select-none" onClick={() => handleSort(col)}>
      {sortKey === col ? (sortAsc ? '↑' : '↓') : '↕'}
    </span>
  )

  return (
    <div className="p-4 space-y-4 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            {isLoading ? (
              <Skeleton className="h-7 w-64" />
            ) : (
              <h1 className="text-xl font-bold">{data?.titulo_reporte ?? 'Dashboard de Asistencias'}</h1>
            )}
            <div className="flex items-center gap-2 mt-1">
              {est?.fecha_evento && (
                <Badge variant="outline" className="text-xs">
                  {new Date(est.fecha_evento + 'T00:00:00').toLocaleDateString('es-EC', {
                    day: '2-digit', month: 'long', year: 'numeric',
                  })}
                </Badge>
              )}
              {est && (
                <Badge style={{ background: estado.color, color: '#fff' }} className="text-xs border-0">
                  {estado.label}
                </Badge>
              )}
              {today && isFetching && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <RefreshCw className="h-3 w-3 animate-spin" /> actualizando
                </span>
              )}
              {today && !isFetching && dataUpdatedAt > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <RefreshCw className="h-3 w-3" /> auto-refresh 5 min
                </span>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting}>
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Total Asistentes */}
        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-blue-500/15 via-blue-500/5 via-50% to-transparent dark:from-blue-800/20 dark:via-blue-900/10 dark:via-50% dark:to-transparent">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Asistentes</CardTitle>
            <Users className="h-4 w-4" style={{ color: TONAL_BLUE }} />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-end gap-2">
                <div className="text-3xl font-bold text-black dark:text-white">
                  <AnimatedCounter value={est?.total_asistentes ?? 0} />
                </div>
                {(est?.porcentaje_asistencia ?? 0) > 0 && (
                  <span className="text-sm font-medium mb-0.5" style={{ color: TONAL_BLUE }}>
                    {est!.porcentaje_asistencia}%
                  </span>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">asistentes únicos verificados</p>
          </CardContent>
        </Card>

        {/* Intervalo Pico */}
        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-green-500/15 via-green-500/5 via-50% to-transparent dark:from-green-800/20 dark:via-green-900/10 dark:via-50% dark:to-transparent">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Intervalo Pico</CardTitle>
            <Clock className="h-4 w-4" style={{ color: TONAL_GREEN }} />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-3xl font-bold text-black dark:text-white">
                {est?.intervalo_pico ?? 'N/A'}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {est?.asistencia_hora_pico ? `${est.asistencia_hora_pico} ingresos` : 'máximo de ingresos'}
            </p>
          </CardContent>
        </Card>

        {/* Estado del Evento */}
        <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-green-500/15 via-green-500/5 via-50% to-transparent dark:from-green-800/20 dark:via-green-900/10 dark:via-50% dark:to-transparent">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Estado del Evento</CardTitle>
            <Activity className="h-4 w-4" style={{ color: TONAL_GREEN }} />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-28" />
            ) : (
              <div className="text-2xl font-bold" style={{ color: estado.color }}>
                {estado.label}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {est?.hora_inicio && est?.hora_fin
                ? `${est.hora_inicio} – ${est.hora_fin}`
                : 'sin horario configurado'}
            </p>
          </CardContent>
        </Card>

      </div>

      {/* Fila 1: Gráfica de líneas (ancho completo) */}
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
        <CardHeader className="shrink-0">
          <CardTitle className="text-sm font-medium">Ingresos por Intervalos de 15 min</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
              Sin datos de horario disponibles
            </div>
          ) : (
            <ChartContainer config={lineChartConfig} className="w-full h-64">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
                <XAxis dataKey="intervalo" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} allowDecimals={false} />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      formatter={(value) => (
                        <span className="text-muted-foreground">
                          Asistentes: {Number(value).toLocaleString('es-EC')}
                        </span>
                      )}
                      hideLabel
                    />
                  }
                />
                <Line
                  type="monotone"
                  dataKey="asistentes"
                  stroke={TONAL_BLUE}
                  strokeWidth={2}
                  dot={{ r: 3, fill: TONAL_BLUE }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Fila 2: Funnel + Por Perfil */}
      <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
        <Card className="lg:col-span-3 border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Funnel de Conversión</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-36 w-full" />
            ) : (
              <>
                <FunnelChart
                  registros={est?.total_registros ?? 0}
                  confirmaciones={est?.total_confirmaciones ?? 0}
                  asistentes={est?.total_asistentes ?? 0}
                />
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between text-xs text-muted-foreground">
                  <span>Conversión total</span>
                  <span className="font-semibold text-black dark:text-white">
                    {est?.porcentaje_conversion ?? 0}%
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-4 border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Por Perfil</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-0 pb-2">
            {isLoading ? (
              <div className="px-6 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : perfiles.length === 0 ? (
              <p className="px-6 text-xs text-muted-foreground">Sin datos de perfil</p>
            ) : (
              <div
                className="overflow-y-auto"
                style={{ maxHeight: `${Math.min(perfiles.length, 8) * 36 + 16}px` }}
              >
              <ChartContainer
                config={perfilChartConfig}
                style={{ height: `${perfiles.length * 44}px` }}
                className="w-full"
              >
                <BarChart
                  accessibilityLayer
                  data={perfilChartData}
                  layout="vertical"
                  barSize={12}
                  barCategoryGap="40%"
                  margin={{ left: 8, right: 56, top: 4, bottom: 4 }}
                >
                  <YAxis
                    dataKey="perfil"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10 }}
                    width={180}
                  />
                  <XAxis dataKey="total" type="number" hide />
                  <Bar
                    dataKey="total"
                    layout="vertical"
                    fill={TONAL_BLUE}
                    radius={3}
                    isAnimationActive={false}
                    activeBar={{ fill: TONAL_BLUE, opacity: 1 }}
                  >
                    <LabelList
                      dataKey="total"
                      position="right"
                      className="fill-foreground"
                      fontSize={10}
                      formatter={(value: number) => value.toLocaleString('es-EC')}
                    />
                  </Bar>
                </BarChart>
              </ChartContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabla de asistentes */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-sm font-medium">
              Listado de Asistentes
              {!isLoading && (
                <span className="ml-2 text-muted-foreground font-normal">
                  ({filtered.length.toLocaleString()})
                </span>
              )}
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar asistente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
                <TableRow>
                  {columnasAsistencia.map((col) => (
                    <TableHead
                      key={col}
                      className="text-xs cursor-pointer whitespace-nowrap"
                      onClick={() => handleSort(col)}
                    >
                      {formatColumnLabel(col)} <SortIcon col={col} />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: numColumnas }).map((_, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={numColumnas} className="text-center text-muted-foreground py-8 text-sm">
                      {search ? 'Sin resultados para la búsqueda.' : 'No hay asistentes registrados.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((a, i) => (
                    <TableRow key={`${a.id_registro}-${i}`} className="text-sm">
                      {columnasAsistencia.map((col) =>
                        col === 'perfil' ? (
                          <TableCell key={col}>
                            {a.perfil ? (
                              <Badge variant="outline" className="text-xs">{a.perfil}</Badge>
                            ) : '-'}
                          </TableCell>
                        ) : (
                          <TableCell
                            key={col}
                            className={`text-muted-foreground${col === 'fecha_asistencia' ? ' whitespace-nowrap' : ''}`}
                          >
                            {formatCellValue(col, a[col] ?? null)}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginación */}
          {!isLoading && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-muted-foreground">
              <span>
                {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} de {sorted.length}
              </span>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === 1} onClick={() => setPage(1)}>
                  <ChevronLeft className="h-3 w-3" /><ChevronLeft className="h-3 w-3 -ml-2" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <span className="px-2 py-1 text-xs">{page} / {totalPages}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === totalPages} onClick={() => setPage(totalPages)}>
                  <ChevronRight className="h-3 w-3" /><ChevronRight className="h-3 w-3 -ml-2" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
